'use client';

import NextLink from 'next/link';
import { useTranslations, useLocale } from 'next-intl';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import MuiLink from '@mui/material/Link';
import useMediaQuery from '@mui/material/useMediaQuery';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';
import { getConsumerUrl } from '../../lib/consumerUrl';
import { getHelpBotSlug } from '../../lib/helpBot';

/**
 * LandingFooter - Professional multi-column footer for the landing page
 *
 * Sections:
 * - About: About Boty, Terms of Service, Privacy Policy, Contact Us
 * - Use Cases: Boty for Events, Services, Personal Brands (anchor links)
 * - Social: Links to social media profiles (placeholder)
 * - Copyright: © 2026 Boty. All rights reserved.
 *
 * Features:
 * - Responsive: 4 columns desktop, stacked on mobile
 * - RTL-aware layout
 * - Minimum 48x48px touch targets on mobile
 *
 * Story 6.7: Landing Page Header And Footer For Credibility
 */
export function LandingFooter() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const locale = useLocale();
  const t = useTranslations('creator.landing.footer');
  const consumerUrl = getConsumerUrl();

  const currentYear = new Date().getFullYear();

  // Footer link style with adequate touch target
  const linkStyle = {
    color: 'text.secondary',
    textDecoration: 'none',
    display: 'block',
    py: isMobile ? 1.5 : 0.75, // Ensure 48px touch target on mobile
    minHeight: isMobile ? 48 : 'auto',
    '&:hover': {
      color: 'primary.main',
      textDecoration: 'underline',
    },
  };

  const sectionTitleStyle = {
    fontWeight: 600,
    mb: 2,
    color: 'text.primary',
  };

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: 'grey.50',
        borderTop: 1,
        borderColor: 'divider',
        mt: 'auto',
        py: { xs: 4, md: 6 },
      }}
    >
      <Container maxWidth="lg">
        <Grid
          container
          spacing={{ xs: 4, md: 4 }}
          sx={{
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'center',
            textAlign: 'center',
          }}
        >
          {/* About Section */}
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography variant="subtitle1" sx={sectionTitleStyle}>
              {t('about.title')}
            </Typography>
            <Box component="nav" aria-label={t('about.title')}>
              <MuiLink
                component={NextLink}
                href={`/${locale}/about`}
                sx={linkStyle}
              >
                {t('about.aboutBoty')}
              </MuiLink>
              <MuiLink
                component={NextLink}
                href={`/${locale}/terms`}
                sx={linkStyle}
              >
                {t('about.terms')}
              </MuiLink>
              <MuiLink
                component={NextLink}
                href={`/${locale}/privacy`}
                sx={linkStyle}
              >
                {t('about.privacy')}
              </MuiLink>
              <MuiLink
                component={NextLink}
                href={`/${locale}/contact`}
                sx={linkStyle}
              >
                {t('about.contact')}
              </MuiLink>
            </Box>
          </Grid>

          {/* Use Cases Section */}
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography variant="subtitle1" sx={sectionTitleStyle}>
              {t('useCases.title')}
            </Typography>
            <Box component="nav" aria-label={t('useCases.title')}>
              <MuiLink
                component={NextLink}
                href={`/${locale}/event-faq-bot`}
                sx={linkStyle}
              >
                {t('useCases.events')}
              </MuiLink>
              <MuiLink
                component={NextLink}
                href={`/${locale}/compare`}
                sx={linkStyle}
              >
                {t('about.compare')}
              </MuiLink>
              <MuiLink
                component={NextLink}
                href={`/${locale}/service-bot`}
                sx={linkStyle}
              >
                {t('useCases.services')}
              </MuiLink>
              <MuiLink
                component={NextLink}
                href={`/${locale}/personal-brand-bot`}
                sx={linkStyle}
              >
                {t('useCases.personalBrands')}
              </MuiLink>
              <MuiLink
                href={`${consumerUrl}/${locale}/samples`}
                target="_blank"
                rel="noopener noreferrer"
                sx={linkStyle}
              >
                {t('useCases.samples')}
              </MuiLink>
            </Box>
          </Grid>

          {/* Social Section */}
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography variant="subtitle1" sx={sectionTitleStyle}>
              {t('social.title')}
            </Typography>
            <Box component="nav" aria-label={t('social.title')}>
              <MuiLink
                component={NextLink}
                href={`/${locale}/blog`}
                sx={linkStyle}
              >
                {t('about.blog')}
              </MuiLink>
            </Box>
          </Grid>

          {/* Support Section */}
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography variant="subtitle1" sx={sectionTitleStyle}>
              {t('support.title')}
            </Typography>
            <Box component="nav" aria-label={t('support.title')}>
              <MuiLink
                href={`${consumerUrl}/${locale}/${getHelpBotSlug(locale)}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={linkStyle}
              >
                {t('support.helpBot')}
              </MuiLink>
              <MuiLink
                component={NextLink}
                href={`/${locale}/help`}
                sx={linkStyle}
              >
                {t('support.helpCenter')}
              </MuiLink>
            </Box>
          </Grid>
        </Grid>

        {/* Copyright */}
        <Divider sx={{ mt: 4, mb: 3 }} />
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            textAlign: 'center',
          }}
        >
          {t('copyright', { year: currentYear })}
        </Typography>
      </Container>
    </Box>
  );
}

export default LandingFooter;
